'use client';

// React
import { type ReactElement } from 'react';

// Hooks
import { useQuantityPicker } from '@/hooks/use-quantity-picker';

// Utils
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { SvgMinus, SvgPlus } from '../icons-eataly';

// Constants

interface QuantityPickerProps {
    /** Current quantity value as string */
    value: string;
    /** Callback when quantity changes */
    onChange: (stringValue: string, numberValue: number) => void;
    /** Callback when input loses focus */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    /** Minimum quantity allowed */
    min?: number;
    /** Maximum quantity allowed (for bonus products, etc.) */
    max?: number;
    /** Product name for accessibility */
    productName?: string;
    /** Whether the picker is disabled */
    disabled?: boolean;
}

/**
 * QuantityPicker - A shadcn/ui implementation based on Chakra UI's NumberInput
 *
 * This component provides a mobile-first quantity selector with:
 * - Increment/decrement buttons
 * - Direct input field
 * - Keyboard navigation support
 * - Accessibility features
 * - Focus management
 * - Auto-correction of invalid values on blur (when no custom onBlur handler is provided)
 */
export default function QuantityPicker({
    value,
    onChange,
    onBlur,
    min = 0,
    max,
    productName,
    disabled = false,
}: QuantityPickerProps): ReactElement {
    const { t: tQuantity } = useTranslation('quantitySelector');
    const { t: tCommon } = useTranslation('common');

    const {
        inputValue,
        inputRef,
        isDecrementDisabled,
        isIncrementDisabled,
        handleIncrement,
        handleDecrement,
        handleInputChange,
        handleInputFocus,
        handleInputBlur,
        handleKeyDown,
    } = useQuantityPicker({
        value,
        onChange,
        onBlur,
        min,
        max,
    });

    return (
        <div className="flex h-[52px] w-[124px] items-center justify-center gap-2 rounded-full border border-[var(--neutral-10)] px-4">
            {/* Decrement Button */}
            <button
                onClick={handleDecrement}
                disabled={disabled || isDecrementDisabled}
                className="text-xs text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed rounded-full cursor-pointer"
                aria-label={tQuantity('decreaseQuantityForProduct', { productName: productName || tCommon('product') })}
                data-testid="quantity-decrement">
                <SvgMinus aria-hidden="true" />
            </button>

            {/* Input Field */}
            <input
                ref={inputRef}
                type="number"
                min={min}
                max={max}
                step={1}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                className={cn(
                    'min-w-0 flex-1 text-center label-l border-0 bg-transparent focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed',
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                )}
                aria-label={tQuantity('quantity')}
            />

            {/* Increment Button */}
            <button
                onClick={handleIncrement}
                disabled={disabled || isIncrementDisabled}
                className="text-xs text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed rounded-full cursor-pointer"
                aria-label={tQuantity('increaseQuantityForProduct', { productName: productName || tCommon('product') })}
                data-testid="quantity-increment">
                <SvgPlus aria-hidden="true" />
            </button>
        </div>
    );
}
